import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories } from '@/mocks/categories';
import { comics, getHotComics, getNewComics, getLatestComics, getRecommendedComics } from '@/mocks/comics';

export default function Home() {
  const [heroIndex, setHeroIndex] = useState(0);
  const hotComics = getHotComics();
  const newComics = getNewComics();
  const latestComics = getLatestComics();
  const recommendedComics = getRecommendedComics();

  const heroComics = hotComics.slice(0, 4);

  useEffect(() => {
    const timer = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % heroComics.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroComics.length]);

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '韩漫大全',
    url: window.location.origin,
    description: '韩漫日漫网站，韩漫大全，好看的韩国漫画，无删减韩漫免费在线观看',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${window.location.origin}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
    inLanguage: 'zh-CN',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫大全',
    url: window.location.origin,
    description: '提供韩漫和日漫在线免费阅读的综合性漫画平台',
    sameAs: [],
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '韩漫大全 - 好看的韩国漫画免费在线观看',
    description: '韩漫日漫网站，韩漫大全，好看的韩国漫画，无删减韩漫免费在线观看。本站同时提供韩漫和日漫资源，拥有丰富的韩漫大全和好看的韩国漫画。',
    url: window.location.href,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫大全',
      url: window.location.origin,
    },
    inLanguage: 'zh-CN',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '韩漫大全提供哪些漫画资源？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫大全提供丰富的韩漫和日漫资源，包括恋爱、奇幻、动作、剧情等多种类型。所有漫画均免费在线观看，无需注册，更新迅速。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫大全的漫画是否无删减？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫大全所有漫画均为无删减版本，完整呈现原作者创作内容，为读者提供最原汁原味的阅读体验。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫大全是否需要付费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不需要。韩漫大全所有漫画资源均为免费在线观看，用户无需支付任何费用即可畅享海量漫画内容。',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[480px] md:h-[620px] overflow-hidden">
          {heroComics.map((comic, idx) => (
            <div
              key={comic.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                idx === heroIndex ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={comic.cover}
                alt={comic.title}
                className="w-full h-full object-cover object-top"
                title={`${comic.title} - 韩漫大全热门推荐`}
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>

          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 max-w-3xl leading-tight">
              韩漫日漫网站 · 韩漫大全
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-xl mb-8 leading-relaxed">
              好看的韩国漫画，无删减韩漫免费在线观看。本站同时提供韩漫和日漫资源，拥有丰富的韩漫大全，更新迅速，为您打造全能阅读平台。
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/category/all"
                className="px-8 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                立即开始阅读
              </Link>
              <Link
                to="/category/korean"
                className="px-8 py-3 rounded-full bg-white/15 backdrop-blur-sm text-white font-semibold text-sm border border-white/30 hover:bg-white/25 transition-colors whitespace-nowrap cursor-pointer"
              >
                浏览韩漫大全
              </Link>
            </div>

            {/* Hero indicators */}
            <div className="absolute bottom-8 flex items-center gap-2">
              {heroComics.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setHeroIndex(idx)}
                  className={`w-2.5 h-2.5 rounded-full transition-all cursor-pointer ${
                    idx === heroIndex ? 'bg-white w-7' : 'bg-white/40 hover:bg-white/60'
                  }`}
                  aria-label={`幻灯片 ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-12 md:py-16 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                漫画分类
              </h2>
              <p className="text-foreground-600 text-sm md:text-base">
                丰富的分类选择，快速找到你喜欢的漫画类型
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-3 p-5 md:p-6 rounded-lg bg-background-100 hover:bg-primary-50 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                    <i className={`${cat.icon} text-xl md:text-2xl`}></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                    {cat.name}
                  </span>
                  <p className="text-xs text-foreground-500 text-center leading-relaxed hidden sm:block">
                    {cat.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Comics */}
        <section className="py-12 md:py-16 px-4 md:px-6 lg:px-8 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                  热门韩漫推荐
                </h2>
                <p className="text-sm text-foreground-600">万人追更，口碑爆棚的热门漫画</p>
              </div>
              <Link
                to="/category/all"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-xs"></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {hotComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} size="medium" />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-12 md:py-16 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                  最新更新
                </h2>
                <p className="text-sm text-foreground-600">每日更新，精彩不停</p>
              </div>
              <Link
                to="/category/all"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-xs"></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {latestComics.slice(0, 12).map((comic) => (
                <ComicCard key={comic.id} comic={comic} size="medium" />
              ))}
            </div>
          </div>
        </section>

        {/* New Works */}
        {newComics.length > 0 && (
          <section className="py-12 md:py-16 px-4 md:px-6 lg:px-8 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                    新作上线
                  </h2>
                  <p className="text-sm text-foreground-600">新鲜出炉，抢先体验</p>
                </div>
                <Link
                  to="/category/all"
                  className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  查看全部
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </div>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {newComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} size="medium" />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Recommended Comics */}
        <section className="py-12 md:py-16 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                编辑精选
              </h2>
              <p className="text-foreground-600 text-sm md:text-base">
                品质保证，每一部都值得细细品味
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {recommendedComics.slice(0, 4).map((comic) => (
                <Link
                  key={comic.id}
                  to={`/article/${comic.id}`}
                  className="group flex gap-4 p-4 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer"
                >
                  <div className="w-20 h-[107px] flex-shrink-0 rounded-md overflow-hidden bg-background-200">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      title={`${comic.title} - 韩漫大全编辑精选`}
                    />
                  </div>
                  <div className="flex flex-col justify-between min-w-0">
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                        </div>
                        {comic.rating}
                      </span>
                      <span>{comic.chapters}话</span>
                      <span className={`font-medium ${comic.status === 'completed' ? 'text-accent-700' : 'text-primary-500'}`}>
                        {comic.status === 'completed' ? '已完结' : '连载中'}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-accent-500">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl md:text-4xl font-bold text-background-50 mb-1">10,000+</div>
                <p className="text-sm text-background-50/80">漫画资源</p>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-bold text-background-50 mb-1">500万+</div>
                <p className="text-sm text-background-50/80">月活跃读者</p>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-bold text-background-50 mb-1">99.8%</div>
                <p className="text-sm text-background-50/80">好评率</p>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-bold text-background-50 mb-1">日更</div>
                <p className="text-sm text-background-50/80">更新频率</p>
              </div>
            </div>
          </div>
        </section>

        {/* Platform Features */}
        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                为什么选择韩漫大全
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
                我们致力于打造最好的中文漫画阅读平台
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-shield-check-line', title: '无删减原版', desc: '所有漫画均为无删减版本，完整呈现原作者创作内容' },
                { icon: 'ri-speed-up-line', title: '极速更新', desc: '与韩国日本同步更新，第一时间带来最新章节' },
                { icon: 'ri-smartphone-line', title: '多端适配', desc: '完美适配电脑、平板、手机，随时随地畅快阅读' },
                { icon: 'ri-vip-crown-line', title: '完全免费', desc: '所有漫画资源免费观看，无需注册无需付费' },
              ].map((feature, idx) => (
                <div key={idx} className="flex flex-col items-center text-center p-6 rounded-lg bg-background-100">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-500 mb-4">
                    <i className={`${feature.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-2">{feature.title}</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
    </>
  );
}